/**
 * @author Vibhor <vibhor.sharma@314ecorp.com>
 * @description Code Widgets
 */
import React, { useState } from 'react';
import _ from 'lodash';
import { Avatar, Flex, Modal, Tag, Tooltip, Typography } from 'antd';
import { Copy, Dot, Help } from '@icon-park/react';
import { TooltipPlacement } from 'antd/lib/tooltip';
import { useNavigate } from 'react-router-dom';

import Button from 'components/widgets/button';
import EventTracker from 'utils/eventTracker';
import PDFViewer from 'components/pdfjs';
import { DETAILED_CODE_VIEW_FIELDS, NO_DATA_PLACEHOLDER } from 'app-constants/codeExplorer';
import { EVENT_ENTITIES, EVENTS } from 'app-constants/events';
import { getCodeSetSearchResultsPath } from 'utils/router';
import { getDigitRequired } from 'utils/codeExplorer/icdParser';
import { useCodeExplorerStoreActions } from 'store/codeExplorer';

/**
 * Code Title Widget
 */
const CodeTitle: React.FC<{ code: string; codeSet?: string; isCopyable?: boolean; modifier?: boolean }> = ({
	code,
	codeSet,
	isCopyable = true,
	modifier = false,
}) => {
	return (
		<Flex align='center'>
			<div className='w-25'>
				<Tag data-testid='ce-widget-code-title' color='#D4E6FF' bordered={false} className='min-w-20'>
					<Typography.Text
						className='flex w-full items-center justify-between'
						copyable={
							isCopyable
								? {
										icon: (
											<Copy
												data-testid='ce-copy-button'
												onClick={() =>
													EventTracker.trackEvent(
														EVENTS.codeExplorer.copyCode(code, [codeSet], EVENT_ENTITIES.codeCard),
													)
												}
												theme='outline'
												size='12'
												fill='#595959'
											/>
										),
									}
								: undefined
						}
					>
						{code}
					</Typography.Text>
				</Tag>
			</div>
			{codeSet && (
				<Flex align='center' className='max-w-35'>
					<Dot theme='filled' size='16' fill='#1890FF' />
					<Typography.Text className='text-orange-secondary'>{codeSet + (modifier ? ' Modifier' : '')}</Typography.Text>
					<Dot theme='filled' size='16' fill='#1890FF' />
				</Flex>
			)}
		</Flex>
	);
};

/**
 * Deleted Code Tag Widget
 */
const DeletedCodeTag: React.FC = () => {
	return (
		<Tag data-testid='ce-widget-deleted-tag' className='text-danger border border-[#FFA39E] bg-[#FFF1F0]'>
			{'Deleted'}
		</Tag>
	);
};

/**
 * Code Avatar Widget
 */
const CodeAvatar: React.FC<{
	code: string;
	hipaaSupport: boolean;
	requiredCharLength: number;
	placement?: TooltipPlacement;
}> = ({ code, hipaaSupport, requiredCharLength, placement = 'top' }) => {
	if (!code) {
		return null;
	}
	const { text, title, isBillable } = getDigitRequired(code, requiredCharLength, hipaaSupport);
	const billableStyle: React.CSSProperties = { backgroundColor: '#95DE64', color: 'white', border: '1px solid #D9F7BE' };
	const nonBillableStyle: React.CSSProperties = { backgroundColor: '#FF7875', color: 'white', border: '1px solid #ffccc7' };
	return (
		<Tooltip title={title} placement={placement} styles={{ root: { maxWidth: '170px' } }}>
			<Avatar className='code-avatar' key={code} size={22} style={isBillable ? billableStyle : nonBillableStyle}>
				{text}
			</Avatar>
		</Tooltip>
	);
};

/**
 * Notes Section Widget
 */
const NotesSection: React.FC<{ notes: string[] }> = ({ notes }) => {
	if (!_.isArray(notes) || _.isEmpty(notes)) {
		return <span>{NO_DATA_PLACEHOLDER}</span>;
	}
	const lengthOfContent = _.sumBy(notes, (note) => _.size(note));
	const shouldShowModal = lengthOfContent > 300;

	let truncatedContent = '';
	for (const note of notes) {
		const remainingLength = 300 - (truncatedContent?.length ?? 0);
		if (remainingLength < 0) {
			break;
		}
		truncatedContent += note?.slice(0, remainingLength) + '\n';
	}
	const fullContent = _.join(notes, '\n');
	return (
		<>
			{shouldShowModal ? truncatedContent : fullContent}
			{shouldShowModal && <Button.ShowICD10Attribute title={'Notes'} content={fullContent} />}
		</>
	);
};

/**
 * Code Links Widget
 */
const CodeLinks: React.FC<{ links: string[]; codeSet: CodeExplorer.ICodeSetSystem }> = ({ links, codeSet }) => {
	const navigate = useNavigate();
	const { resetSearchPagination } = useCodeExplorerStoreActions();

	if (!_.isArray(links) || _.isEmpty(links)) {
		return null;
	}

	return (
		<>
			{_.map(links, (link, index) => (
				<React.Fragment key={`link-${index}`}>
					<span>(</span>
					<Typography.Link
						underline
						onClick={() => {
							navigate(getCodeSetSearchResultsPath({ codeSet, searchTerm: link }));
							resetSearchPagination();
						}}
					>
						{link}
					</Typography.Link>
					<span>)</span>
					{index < links.length - 1 && <span>{', '}</span>}
				</React.Fragment>
			))}
		</>
	);
};

/**
 * SubField Row Widget
 */
const SubFieldRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => {
	return (
		<Flex>
			<div className='flex-1 px-4 py-2'>{label}</div>
			<div className='flex-2 p-2 pl-0'>{children}</div>
		</Flex>
	);
};

/**
 * Text Links Array Field Widget
 */
interface TextLinksArrayFieldProps {
	data: { text: string; link?: string[] }[];
	codeSet: CodeExplorer.ICodeSetSystem;
	title: string;
	maxItems?: number;
}

const TextLinksArrayField: React.FC<TextLinksArrayFieldProps> = ({ data, codeSet, title, maxItems = 3 }) => {
	if (_.isEmpty(data)) {
		return <span>{NO_DATA_PLACEHOLDER}</span>;
	}

	const shouldShowModal = data.length > maxItems;
	const displayItems = shouldShowModal ? data.slice(0, maxItems) : data;

	const fullContent = (
		<>
			{_.map(data, (item, index) => (
				<div key={index}>
					<span>{item.text}&nbsp;</span>
					{item.link && <CodeLinks links={item.link} codeSet={codeSet} />}
				</div>
			))}
		</>
	);

	const truncatedContent = (
		<>
			{_.map(displayItems, (item, index) => (
				<div key={index}>
					<span>{item.text}&nbsp;</span>
					{item.link && <CodeLinks links={item.link} codeSet={codeSet} />}
				</div>
			))}
		</>
	);

	return (
		<>
			{shouldShowModal ? truncatedContent : fullContent}
			{shouldShowModal && <Button.ShowICD10Attribute title={title} content={fullContent} />}
		</>
	);
};

/**
 * Guideline Section Header Widget
 */
interface GuidelineSectionHeaderProps {
	field: CodeExplorer.IDetailedCodeField;
}

const GuidelineSectionHeader: React.FC<GuidelineSectionHeaderProps> = ({ field }) => {
	return (
		<div key={field.key} className='w-full border-r border-[#D4E6FF] bg-[#FAFAFA] p-2 whitespace-pre-line'>
			<Flex gap={8}>
				<Typography.Text className='text-neutral-7 m-0 font-semibold'>{field.label}</Typography.Text>
				<Tooltip title={field.description}>
					<Help theme='filled' size='16' fill='#8C8C8C' />
				</Tooltip>
			</Flex>
		</div>
	);
};

/**
 * Seven Character Note Widget
 */
interface SevenCharacterNoteProps {
	sevenCharacterNote: string;
	sevenCharacterDef: Record<string, string>;
}

const SevenCharacterNote: React.FC<SevenCharacterNoteProps> = ({ sevenCharacterNote, sevenCharacterDef }) => {
	if (!sevenCharacterNote) {
		return <span>{NO_DATA_PLACEHOLDER}</span>;
	}
	return (
		<>
			<span>{sevenCharacterNote}</span>
			<br />
			{sevenCharacterDef && (
				<>
					{_.map(sevenCharacterDef, (item, key) => (
						<div key={key}>
							{key}: {item}
						</div>
					))}
				</>
			)}
		</>
	);
};

/**
 * Guideline Renderer Widget
 */
interface GuidelineRendererProps {
	parsedGuidelines: CodeExplorer.IGuidelines;
	field: CodeExplorer.IDetailedCodeField;
	codeSet: CodeExplorer.ICodeSetSystem;
	searchValue?: string;
}

const GuidelineRenderer: React.FC<GuidelineRendererProps> = ({ parsedGuidelines, field, codeSet, searchValue = '' }) => {
	const isCodeGuidelines = field.key === DETAILED_CODE_VIEW_FIELDS.ICD10CM.codeGuidelines.key;
	const isSectionGuidelines = field.key === DETAILED_CODE_VIEW_FIELDS.ICD10CM.sectionGuidelines.key;

	const shouldShowField = (label: string): boolean => {
		if (!searchValue) {
			return true;
		}
		return _.toLower(label).includes(_.toLower(searchValue));
	};

	const fieldsConfig = [
		{
			label: 'Description',
			shouldRender: () => shouldShowField('Description'),
			render: () => (
				<>
					<span>{parsedGuidelines?.description?.text ?? NO_DATA_PLACEHOLDER}&nbsp;</span>
					{parsedGuidelines?.description?.link && <CodeLinks links={parsedGuidelines?.description?.link} codeSet={codeSet} />}
				</>
			),
		},
		{
			label: 'Notes',
			shouldRender: () => shouldShowField('Notes'),
			render: () => <NotesSection notes={parsedGuidelines?.notes} />,
		},
		{
			label: 'Inclusion Term',
			shouldRender: () => (isCodeGuidelines || isSectionGuidelines) && shouldShowField('Inclusion Term'),
			render: () => <NotesSection notes={parsedGuidelines?.inclusionTerm} />,
		},
		{
			label: 'Includes',
			shouldRender: () => shouldShowField('Includes'),
			render: () => (
				<TextLinksArrayField data={parsedGuidelines?.includeExcludes?.includes} codeSet={codeSet} title='Includes' maxItems={3} />
			),
		},
		{
			label: 'Use Additional Code',
			shouldRender: () => shouldShowField('Use Additional Code'),
			render: () => (
				<TextLinksArrayField
					data={parsedGuidelines?.useAdditionalCode}
					codeSet={codeSet}
					title='Use Additional Code'
					maxItems={2}
				/>
			),
		},
		{
			label: 'Code First',
			shouldRender: () => shouldShowField('Code First'),
			render: () => <TextLinksArrayField data={parsedGuidelines?.codeFirst} codeSet={codeSet} title='Code First' maxItems={2} />,
		},
		{
			label: 'Code Also',
			shouldRender: () => shouldShowField('Code Also'),
			render: () => <TextLinksArrayField data={parsedGuidelines?.codeAlso} codeSet={codeSet} title='Code Also' maxItems={3} />,
		},
		{
			label: 'Excludes 1',
			shouldRender: () => shouldShowField('Excludes 1'),
			render: () => (
				<TextLinksArrayField
					data={parsedGuidelines?.includeExcludes?.excludes1}
					codeSet={codeSet}
					title='Excludes 1'
					maxItems={3}
				/>
			),
		},
		{
			label: 'Excludes 2',
			shouldRender: () => shouldShowField('Excludes 2'),
			render: () => (
				<TextLinksArrayField
					data={parsedGuidelines?.includeExcludes?.excludes2}
					codeSet={codeSet}
					title='Excludes 2'
					maxItems={3}
				/>
			),
		},
		{
			label: 'Seventh Character Note',
			shouldRender: () => isCodeGuidelines && shouldShowField('Seventh Character Note'),
			render: () => (
				<SevenCharacterNote
					sevenCharacterNote={parsedGuidelines?.sevenCharacterNote}
					sevenCharacterDef={parsedGuidelines?.sevenCharacterDef}
				/>
			),
		},
	];

	const visibleFields = _.filter(fieldsConfig, (config) => config.shouldRender());

	return (
		<Flex>
			<Flex flex={1}>
				<GuidelineSectionHeader field={field} />
			</Flex>
			<Flex vertical flex={3}>
				{_.map(visibleFields, (config, index) => (
					<SubFieldRow key={`${config.label}-${index}`} label={config.label}>
						{config.render()}
					</SubFieldRow>
				))}
			</Flex>
		</Flex>
	);
};

/**
 * Joined Array Widget
 */
interface JoinedArrayProps {
	items: string[];
	separator?: string;
}

const JoinedArray: React.FC<JoinedArrayProps> = ({ items, separator = ', ' }) => {
	if (!_.isArray(items) || _.isEmpty(items)) {
		return <span>{NO_DATA_PLACEHOLDER}</span>;
	}

	return <span>{_.join(items, separator)}</span>;
};

/**
 * Image Widget Component
 */
const ImageWidget: React.FC<{ imageSrc: () => string; description: string; 'data-testid'?: string }> = ({
	imageSrc,
	description,
	'data-testid': dataTestId,
}) => {
	return (
		<Flex data-testid={dataTestId} vertical justify='center' align='center'>
			<img src={imageSrc()} className='h-50 w-50' alt='' />
			<Flex vertical justify='center' align='center'>
				<Typography.Title level={5} type='secondary' className='m-0'>
					{description}
				</Typography.Title>
			</Flex>
		</Flex>
	);
};

/**
 * Tag Widget Component
 */
const TagWidget: React.FC<{ icon: () => string; description: string }> = ({ icon, description }) => {
	return (
		<Tag color='#F0F7FF' className='code-explorer-legend-tag'>
			<Flex gap={10} align='center' justify='space-around'>
				<img src={icon()} alt='' />
				<span className='legend-description'>{description}</span>
			</Flex>
		</Tag>
	);
};

/**
 * MSDRGTags Widget Component
 */
const MSDRGTags: React.FC<{
	medicareSeverityDetail?: CodeExplorer.ICodeDisplayItem;
	mdcDetail?: CodeExplorer.ICodeDisplayItem;
}> = ({ medicareSeverityDetail, mdcDetail }) => {
	return (
		<>
			{mdcDetail?.code && (
				<>
					<Tag key='mdc'>
						{'MDC- ' + mdcDetail?.code}: {mdcDetail?.display}
					</Tag>
					<Dot theme='filled' size='16' fill='#1890FF' />
				</>
			)}
			{medicareSeverityDetail?.code && (
				<Tag key='medicareSeverity'>
					{medicareSeverityDetail?.code}: {medicareSeverityDetail?.display}
				</Tag>
			)}
		</>
	);
};

/**
 * Simple Text Field Widget
 */
const SimpleTextField: React.FC<{ data: string | null }> = ({ data }) => {
	return <span>{data ?? NO_DATA_PLACEHOLDER}</span>;
};

/**
 * Code Display Field Widget
 */
const CodeDisplayField: React.FC<{ data: CodeExplorer.ICodeDisplayItem | null }> = ({ data }) => {
	if (!data) {
		return <span>{NO_DATA_PLACEHOLDER}</span>;
	}
	return (
		<Typography.Text>
			{data.code} - {data.display}
		</Typography.Text>
	);
};

/**
 * Code Display Array Field Widget
 */
const CodeDisplayArrayField: React.FC<{ data: CodeExplorer.ICodeDisplayItem[] }> = ({ data }) => {
	if (_.isEmpty(data)) {
		return <span>{NO_DATA_PLACEHOLDER}</span>;
	}
	return (
		<Flex vertical gap={8}>
			{_.map(data, (item) => (
				<Flex key={item.code}>
					<Typography.Text>
						{item.code} - {item.display}
					</Typography.Text>
				</Flex>
			))}
		</Flex>
	);
};

/**
 * Button Array Field Widget
 */
const ButtonArrayField: React.FC<{ data: CodeExplorer.IButtonArrayItem[] }> = ({ data }) => {
	if (_.isEmpty(data)) {
		return <span>{NO_DATA_PLACEHOLDER}</span>;
	}
	return (
		<Flex gap={8}>
			{_.map(data, (item) => (
				<Flex key={item.code}>
					<Button.ShowCodeAttributeDetails code={item.code} htmlString={item.content} />
				</Flex>
			))}
		</Flex>
	);
};

/**
 * Guideline PDF Viewer Widget
 */
const GuidelinePDFViewer: React.FC<{ pdfUrl: string | null; fileName: string }> = ({ pdfUrl, fileName }) => {
	const [showPDFViewer, setShowPDFViewer] = useState(false);
	if (!pdfUrl) {
		return <span>{NO_DATA_PLACEHOLDER}</span>;
	}
	return (
		<>
			<Button size='small' type='link' className='p-0' onClick={() => setShowPDFViewer((prev) => !prev)}>
				{fileName ? _.startCase(fileName) : 'View'}
			</Button>
			{showPDFViewer && (
				<Modal
					open={showPDFViewer}
					onCancel={() => setShowPDFViewer(false)}
					footer={null}
					width='80vw'
					className='top-0 rounded-lg p-0'
					styles={{
						body: {
							height: '80vh',
							padding: 0,
							overflow: 'hidden',
							borderRadius: '8px',
						},
						mask: {
							backgroundColor: '#000000CC',
						},
					}}
				>
					<PDFViewer file={pdfUrl} width='80vw' height='80vh' showThumbnails={true} fileName={fileName} />
				</Modal>
			)}
		</>
	);
};

/**
 * Tabular Code View Widget
 */
interface TabularCodeViewProps<T> {
	headers: string[];
	flexValues: number[];
	data: T[];
	renderRow: (item: T, index: number) => React.ReactNode[];
	getRowKey?: (item: T, index: number) => string;
	headerPadding?: string;
	cellPadding?: string;
	showCellBorder?: boolean;
	showContainerBorder?: boolean;
}

// eslint-disable-next-line comma-spacing
const TabularCodeView = <T,>({
	headers,
	flexValues,
	data,
	renderRow,
	getRowKey = (__, index) => _.toString(index),
	headerPadding = '12px 8px',
	cellPadding = '12px 16px',
	showCellBorder = true,
	showContainerBorder = true,
}: TabularCodeViewProps<T>): React.ReactNode => {
	const defaultHeaderStyle: React.CSSProperties = {
		padding: headerPadding,
		backgroundColor: '#D4E6FF',
		fontWeight: 600,
	};

	const defaultCellStyle: React.CSSProperties = {
		padding: cellPadding,
		borderBottom: showCellBorder ? '1px solid #D4E6FF' : 'none',
		whiteSpace: 'pre-line',
	};

	const containerStyle: React.CSSProperties = {
		borderRadius: '8px',
		overflow: 'hidden',
		backgroundColor: '#FFFFFF',
		border: '1px solid #D4E6FF',
		...(!showContainerBorder && { borderBottom: 'none' }),
	};

	return (
		<div style={containerStyle}>
			{/* Header */}
			<Flex>
				{_.map(headers, (header, index) => (
					<div key={index} style={{ ...defaultHeaderStyle, flex: flexValues[index] || 1 }}>
						<Typography.Title level={5} className='text-neutral-8 m-0 font-semibold'>
							{header}
						</Typography.Title>
					</div>
				))}
			</Flex>

			{/* Data Rows */}
			{_.map(data, (item, index) => {
				const cellContents = renderRow(item, index);
				return (
					<Flex key={getRowKey(item, index)}>
						{_.map(cellContents, (content, cellIndex) => (
							<div key={cellIndex} style={{ ...defaultCellStyle, flex: flexValues[cellIndex] || 1 }}>
								{content}
							</div>
						))}
					</Flex>
				);
			})}
		</div>
	);
};

const VideoWidget: React.FC<{
	videoSrc: string;
	poster?: string;
	preload?: 'none' | 'auto';
	style?: React.CSSProperties;
	'data-testid'?: string;
}> = ({ videoSrc, poster, preload = 'none', style, 'data-testid': dataTestId }) => {
	const defaultStyle: React.CSSProperties = { height: '40vh', width: '100%' };
	return (
		<video data-testid={dataTestId} poster={poster} preload={preload} controls style={style ?? defaultStyle}>
			<source src={videoSrc} type='video/mp4' />
			<track kind='captions' />
		</video>
	);
};

const CopyrightFooter: React.FC = () => {
	return (
		<div data-testid='ce-widget-copyright' className='text-center'>
			<Typography.Text type='secondary' className='text-xs'>
				{'CPT® only copyright 2024 American Medical Association. All rights Reserved.'}
			</Typography.Text>
		</div>
	);
};

const TitleWidget: React.FC<{
	title: string;
	codeSetSystem: CodeExplorer.ICodeSetSystem[];
	showSearchButton: boolean;
	showRequiredIndicator?: boolean;
}> = ({ title, codeSetSystem, showSearchButton, showRequiredIndicator = false }) => {
	return (
		<Flex gap={4}>
			<Flex gap={4}>
				{title} {showRequiredIndicator && <span className='text-red-500'>*</span>}
			</Flex>
			{showSearchButton && <Button.SearchClaimCodes codeSetSystem={codeSetSystem} />}
		</Flex>
	);
};

const HIPPSTag: React.FC<{ paymentSystemIndicatorDetail?: CodeExplorer.ICodeDisplayItem }> = ({ paymentSystemIndicatorDetail }) => {
	return (
		<Tag key='paymentSystemIndicator'>
			{paymentSystemIndicatorDetail?.code}: {paymentSystemIndicatorDetail?.display}
		</Tag>
	);
};

const RequiredTitle: React.FC<{ title: string; required?: boolean; asteriskClassName?: string }> = ({
	title,
	required,
	asteriskClassName = 'text-red-500',
}) => (
	<span>
		{title} {required ? <span className={asteriskClassName}>*</span> : null}
	</span>
);

export {
	CodeTitle,
	NotesSection,
	DeletedCodeTag,
	CodeLinks,
	JoinedArray,
	ImageWidget,
	TagWidget,
	CodeAvatar,
	SubFieldRow,
	TextLinksArrayField,
	GuidelineSectionHeader,
	GuidelineRenderer,
	SimpleTextField,
	CodeDisplayField,
	CodeDisplayArrayField,
	ButtonArrayField,
	VideoWidget,
	CopyrightFooter,
	GuidelinePDFViewer,
	MSDRGTags,
	TitleWidget,
	HIPPSTag,
	RequiredTitle,
	TabularCodeView,
};
